// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import Vue from 'vue'
import App from './App'
import { Notification } from '@/core/notification.js'
import pressAndLongPress from './directives/pressAndLongPress'
import { RADAR_EXECUTION_INTERVAL_MS } from '@/constants.js'
const notification = new Notification()

import store from './store'
import router from './router'

import * as Sentry from '@sentry/vue'

// Configurar el store para el manejo de errores de almacenamiento
window.$store = store

function isSentryLogsEnabled() {
    try {
        const fromStore = store.getters.getSentryLogs
        const enabledValues = [true, 'true', 1, '1']
        const disabledValues = [false, 'false', 0, '0']
        if (enabledValues.includes(fromStore)) return true
        if (disabledValues.includes(fromStore)) return false
        const fromStorage = localStorage.getItem('sentryLogs')
        return fromStorage === 'true' || fromStorage === '1'
    } catch {
        const fromStorage = localStorage.getItem('sentryLogs')
        return fromStorage === 'true' || fromStorage === '1'
    }
}

function getSentryContextFromStore() {
    try {
        const uuid = store.getters.getUuid
        const version = store.getters.getVersion
        const coordinates = store.getters.getGeoLocation
        const location = store.getters['login/getLocation'] ? JSON.stringify(store.getters['login/getLocation']) : null
        const account = store.getters['login/getAccount']
        return { uuid: uuid || null, version: version || null, coordinates: coordinates || null, location: location || null, account: account || null }
    } catch {
        return { uuid: null, version: null, coordinates: null, location: null, account: null }
    }
}

function enrichSentryEvent(event) {
    const context = getSentryContextFromStore()
    event.contexts = event.contexts || {}
    event.contexts.custom = event.contexts.custom || {}

    Object.keys(context).forEach((key) => {
        const value = context[key]
        if (value !== null && value !== undefined) {
            event.contexts.custom[key] = value
            if (key === 'uuid') {
                event.user = event.user || {}
                event.user.id = event.user.id || value
            }
        }
    })

    return event
}

const replayEnabled = isSentryLogsEnabled()
const integrations = [
    Sentry.browserTracingIntegration(),
    ...(replayEnabled
        ? [
              Sentry.replayIntegration({
                  maskAllText: false,
                  maskAllInputs: false,
                  blockAllMedia: false,
                  networkDetailAllowUrls: ['localhost', /^https:\/\/andyapp\.io/, /^https:\/\/labtowin\.com/],
                  networkRequestHeaders: ['authorization'],
                  networkResponseHeaders: []
              })
          ]
        : [])
]
Sentry.init({
    Vue,
    dsn: process.env.VUE_APP_SENTRY_DSN,
    environment: process.env.NODE_ENV === 'production' ? 'production' : 'development',
    integrations,
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/andyapp\.io/, /^https:\/\/labtowin\.com/],
    replaysSessionSampleRate: replayEnabled ? 0.1 : 0,
    replaysOnErrorSampleRate: replayEnabled ? 1.0 : 0,
    ignoreErrors: [/ResizeObserver loop/],
    beforeSend(event) {
        if (!isSentryLogsEnabled()) return null
        return enrichSentryEvent(event)
    },
    beforeSendTransaction(event) {
        if (!isSentryLogsEnabled()) return null
        return enrichSentryEvent(event)
    }
})

import { configureFormatters } from '@/utils/formatters'
import { configureDateFormatters, formatDisplayDateFromStore } from '@/utils/dateFormatters'

configureFormatters({
    localeGetter: () => store.getters['intl/getLocale'],
    currencyGetter: () => store.getters['intl/getCurrency']
})

configureDateFormatters({
    configGetter: () => store.getters['login/getConfig'],
    localeGetter: () => {
        try {
            return localStorage.language || store.getters['intl/getLocale']
        } catch (_) {
            return store.getters['intl/getLocale']
        }
    },
    locationNowGetter: () => store.getters['timer/getLocationNow']
})

Vue.filter('formatDate', (value, options) => formatDisplayDateFromStore(value, options))

// Función global para probar errores de almacenamiento
window.testStorageErrors = async function () {
    console.log('🧪 Iniciando pruebas de errores de almacenamiento...')

    try {
        const { db_login, db_tasks } = await import('@/database.js')

        // Prueba 1: Error básico
        console.log('🔥 Probando error básico...')
        try {
            const testData = {
                message: 'Prueba de error básico',
                data: 'x'.repeat(1000000) // 1MB
            }
            await db_login.setItem('test-basic', testData)
            console.log('✅ Prueba básica completada sin errores')
        } catch (error) {
            console.log('❌ Error capturado correctamente:', error.name)
        }

        // Prueba 2: Error de cuota
        console.log('💾 Probando error de cuota...')
        try {
            // Crear un objeto grande que pueda generar error de cuota
            const largeData = {
                data: 'x'.repeat(1024 * 1024 * 50), // 50MB - más realista
                timestamp: new Date().toISOString()
            }
            console.log('🔍 Intentando guardar objeto de 50MB...')
            console.log('🔍 Tamaño aproximado:', largeData.data.length, 'bytes')
            await db_login.setItem('test-quota', largeData)
            console.log('✅ Objeto grande guardado exitosamente')
        } catch (error) {
            console.log('❌ Error de cuota capturado en main.js:', error)
            console.log('❌ Tipo de error:', error.name)
            console.log('❌ Mensaje de error:', error.message)
            // El error ya fue manejado en database.js, aquí solo lo registramos
        }

        // Prueba 3: Error más pequeño para verificar
        console.log('🧪 Probando con objeto más pequeño...')
        try {
            const smallData = {
                data: 'x'.repeat(1024 * 1024 * 10), // 10MB
                timestamp: new Date().toISOString()
            }
            console.log('🔍 Intentando guardar objeto de 10MB...')
            await db_login.setItem('test-small', smallData)
            console.log('✅ Objeto pequeño guardado exitosamente')
        } catch (error) {
            console.log('❌ Error pequeño capturado en main.js:', error)
        }

        // Prueba 4: Llenar cuota con múltiples objetos
        console.log('📦 Probando llenar cuota con múltiples objetos...')
        try {
            for (let i = 0; i < 100; i++) {
                const fillData = {
                    data: 'x'.repeat(1024 * 1024 * 100), // 100MB por objeto
                    timestamp: new Date().toISOString(),
                    index: i
                }
                console.log(`🔍 Guardando objeto ${i + 1}/100 (100MB)...`)
                await db_login.setItem(`test-fill-${i}`, fillData)
            }
            console.log('✅ Todos los objetos guardados exitosamente')
        } catch (error) {
            console.log('❌ Error de cuota capturado en main.js:', error)
            console.log('❌ Tipo de error:', error.name)
            console.log('❌ Mensaje de error:', error.message)
        }

        // Prueba 3: Verificar store
        console.log('📊 Verificando store...')
        if (window.$store && window.$store.state) {
            const error = window.$store.state.storageError
            if (error) {
                console.log('✅ Error en store:', error.message)
            } else {
                console.log('ℹ️ No hay errores en el store')
            }
        } else {
            console.log('❌ Store no disponible')
        }

        // Prueba 4: Simular error en store
        console.log('👁️ Probando visualización...')
        if (window.$store && window.$store.commit) {
            const testError = {
                message: 'Error de prueba para verificar la visualización',
                operation: 'guardar',
                storeName: 'test-store',
                timestamp: new Date().toISOString(),
                originalError: 'TestError'
            }

            window.$store.dispatch('addStorageError', testError)
            console.log('✅ Error simulado en el store')

            // Limpiar después de 3 segundos
            setTimeout(() => {
                window.$store.commit('setStorageError', null)
                console.log('✅ Error limpiado del store')
            }, 3000)
        }

        console.log('🎉 Todas las pruebas completadas')
    } catch (error) {
        console.error('❌ Error en las pruebas:', error)
    }
}

// Función para verificar configuración
window.checkStorageConfig = function () {
    console.log('🔍 Verificando configuración...')

    const checks = {
        store: !!window.$store,
        database: !!(window.$store && window.$store.state && window.$store.state.storageError),
        mutation: !!(window.$store && window.$store.commit)
    }

    console.log('📋 Estado de la configuración:', checks)

    if (checks.store && checks.database) {
        console.log('✅ Configuración correcta')
    } else {
        console.log('❌ Configuración incompleta')
    }

    return checks
}

// Función para limpiar errores
window.clearStorageError = function () {
    if (window.$store && window.$store.commit) {
        window.$store.commit('setStorageError', null)
        console.log('✅ Error del store limpiado')
    } else {
        console.log('❌ Store no disponible')
    }
}

import _ from 'lodash' // options is optional

// MIXINS
import mixin from './core/mixin'
import { isSsoLogoutRedirect } from '@/utils/ssoLogout'

// LANGUAGE
import i18n from './i18n'

// PLUGINS PROPIOS
import popup from './plugins/popup'
import overlay from './plugins/overlay'
import loader from './plugins/loader'
import bottombar from './plugins/bottomBar'
import snackbar from './plugins/snackbar'
import columnFilter from './plugins/columnFilter'
import splashscreen from './plugins/splashscreen'

// PLUGINS AJENOS
// import Vuetify from 'vuetify'
import FunctionalCalendar from 'vue-functional-calendar'
import localizedCalendar from './plugins/localizedCalendar'
import VueMq from 'vue-mq'
// import VueSpeech from 'vue-speech'

import 'es6-promise/auto'
import _refiner from 'refiner-js'

//ALLOW TO ZOOM IN AND OUT IN MOBILE DEVICES
import panZoom from 'vue-panzoom'
Vue.use(panZoom)

// all Cloudinary components:
import Cloudinary from 'cloudinary-vue'
import VueApexCharts from 'vue-apexcharts'
import Skeleton from './components/domain/ui/Skeleton.vue'
import SkeletonLayout from './components/domain/ui/SkeletonLayout.vue'
Vue.use(VueApexCharts)

Vue.component('apexchart', VueApexCharts)
Vue.component('Skeleton', Skeleton)
Vue.component('SkeletonLayout', SkeletonLayout)

window.Vue = Vue

Vue.use(i18n)

// Vue.use(Vuetify)
Vue.use(popup)
Vue.use(overlay)
Vue.use(loader)
Vue.use(bottombar)
Vue.use(snackbar)
Vue.use(columnFilter)
Vue.use(splashscreen)
Vue.use(pressAndLongPress, { longPressDuration: 200 })
// Vue.use(VueSpeech)

Vue.use(FunctionalCalendar, {
    dayNames: i18n.t('calendar.day_names'),
    monthNames: i18n.t('calendar.months')
})
Vue.use(localizedCalendar)

Vue.use(VueMq, {
    breakpoints: {
        portrait: 901,
        landscape: 1200,
        desktop: Infinity
    }
})
Vue.use(Cloudinary, {
    configuration: { cloudName: 'intowin' }
})

const refinerProjectId = process.env.VUE_APP_REFINER_PROJECT_ID
if (refinerProjectId) {
    _refiner('setProject', refinerProjectId)
}

Vue.config.productionTip = false
// Vue.config.devtools = true //Only for debug

/* CUSTOM DIRECTIVES */
Vue.directive('focus', {
    // When the bound element is inserted into the DOM...
    inserted: function (el) {
        // Focus the element
        el.focus()
    }
    // Ejemplo en process de popup
    // https://vuejs.org/v2/guide/custom-directive.html
    // componentUpdated: function (el, binding) {
    //   if( binding.value ){
    //     el.focus();
    //   }
    // },
})

import runtime from 'serviceworker-webpack-plugin/lib/runtime'

if ('serviceWorker' in navigator && process.env.VUE_APP_IS_APP != 'TRUE') {
    runtime.register()
}

/* eslint-disable no-new */

// import { setCacheNameDetails } from 'workbox-core';

// setCacheNameDetails({
//     prefix: 'andy',
//     suffix: 'v1',
//     precache: 'precacheandy',
//     runtime: 'precacheandy',
//     //   googleAnalytics: 'custom-google-analytics-name'
// });
// main

// if ('serviceWorker' in navigator) {
//     navigator.serviceWorker.getRegistrations().then(function (registrations) {
//         for (let registration of registrations) {
//             registration.unregister()
//         }
//     })
//     // navigator.serviceWorker
//     //     .register('service-worker.js')
//     //     .then(console.log)
//     //     .catch(console.error)
// }

// self.addEventListener('activate', function (event) {

//     var cacheAllowlist = ['pages-cache-v1', 'blog-posts-cache-v1'];

//     event.waitUntil(
//         caches.keys().then(function (cacheNames) {
//             return Promise.all(
//                 cacheNames.map(function (cacheName) {
//                     if (cacheAllowlist.indexOf(cacheName) === -1) {
//                         return caches.delete(cacheName);
//                     }
//                 })
//             );
//         })
//     );
// });

// self.addEventListener('activate', event => {
//     alert('hi')
//     console.log('V1 now ready to handle fetches!')
// })

window.handleOpenURL = function (url) {
    console.log('received url: ' + url)
    url = url.replace('andyapp://', '/')
    router.push(url)
}

// service worker
addEventListener('message', (messageEvent) => {
    if (messageEvent.data === 'skipWaiting') return skipWaiting()
})

addEventListener('fetch', (event) => {
    event.respondWith(
        (async () => {
            if (event.request.mode === 'navigate' && event.request.method === 'GET' && registration.waiting && (await clients.matchAll()).length < 2) {
                registration.waiting.postMessage('skipWaiting')
                return new Response('', { headers: { Refresh: '0' } })
            }
            return (await caches.match(event.request)) || fetch(event.request)
        })()
    )
})

/* BLE PLUGIN
function fnEndInitializationApp() {
    // VALIDE IF DEVICE IS WITH CURRENT VERSION
    if (!store.getters['printer/getDeviceNewConfiguration']) {
        if (store.getters.getIsApp) {
            store.dispatch('printer/updateDeviceToNewConfiguration')
        } else {
            store.commit('printer/setDeviceNewConfiguration', true)
        }
    }
}

function fnRequestBTConnect() {
    bluetoothle.hasPermissionBtConnect((permission) => {
        if (permission.hasPermission) {
            localStorage.setItem('bluetoothle_enabled', 1)
            fnEndInitializationApp()
        } else {
            bluetoothle.requestPermissionBtConnect(
                (data) => {
                    if (data && data.requestPermission) {
                        localStorage.setItem('bluetoothle_enabled', 1)
                        fnEndInitializationApp()
                    } else {
                        localStorage.setItem('bluetoothle_enabled', 0)
                        fnEndInitializationApp()
                    }
                },
                (err) => {
                    console.error('fnRequestBTConnect - requestPermissionBtConnect', err)
                    fnEndInitializationApp()
                }
            )
        }
    })
}

function fnRequestBTScanPermission() {
    bluetoothle.hasPermissionBtScan((permission) => {
        if (permission.hasPermission) {
            fnRequestBTConnect()
        } else {
            bluetoothle.requestPermissionBtScan(
                (data) => {
                    if (data && data.requestPermission) {
                        fnRequestBTConnect()
                    } else {
                        localStorage.setItem('bluetoothle_enabled', 0)
                    }
                },
                (err) => {
                    console.error('fnRequestBTScanPermission - requestPermissionBtScan', err)
                }
            )
        }
    })
}

function initRequestBTPermissions() {
    // var params = {
    //     request: true,
    //     statusReceiver: false,
    //     restoreKey: 'andyappcc'
    // }
    // bluetoothle.initialize(function (data) {
    //     console.log('BLE Initialize status:', data)
    //     if (data && data.status == 'enabled') {
    //         fnRequestBTScanPermission()
    //     } else {
    //         // TODO SHOW ERROR INIT BLE
    //         console.error('BLE Initialize Error', data)

    //         localStorage.setItem('bluetoothle_enabled', 1)

    //     }
    // }, params)
    fnEndInitializationApp()
}*/

// end service worker

window.skipLocalNotificationReady = true

const syncRadar = () => {
    log('Syncing Radar in background')
    store.dispatch('radar/syncRadar').catch((error) => {
        logError('Error syncing Radar in background:', error)
    })
}

setInterval(syncRadar, RADAR_EXECUTION_INTERVAL_MS)

// SET EVENTS TO REGISTER USER INTERACTIONS
log('REGISTRANDO EVENTOS DE INTERACCIONES DE USUARIO')
document.addEventListener(
    'visibilitychange',
    function () {
        if (document.hidden) {
            log('--- ANDY HIDDEN ---')
        } else {
            log('--- ANDY ACTIVE AGAIN ---')
            syncRadar()
            if (store.getters.getIsApp) {
                cordova.plugins.brotherprinter.getAutoTimeEnable(
                    function (enable) {
                        store.commit('setAutoTimeEnable', enable)
                    },
                    function (err) {
                        logError('ERROR AL OBTENER EL PARAMETRO' + err.toString())
                    }
                )
                cordova.plugins.brotherprinter.getAirPlaneModeEnable(
                    function (enable) {
                        store.commit('setAirPlaneModeEnable', enable)
                    },
                    function (err) {
                        logError('ERROR AL OBTENER EL PARAMETRO' + err.toString())
                    }
                )
            }

            // Check if the timezone difference and device time are different
            store.dispatch('checkDateTimezoneDifference')

            let last_action = store.getters.getLastEmployeeAction
            log('LAST ACTION EMPLOYEE DETECTED', last_action, moment().valueOf() - store.getters.getTimeout * 1000)

            if (last_action && last_action.time < moment().valueOf() - store.getters.getTimeout * 1000) {
                const employee_id = store.getters['loginUser/getLocalEmployee']
                const plsMode = localStorage.getItem('plsMode') === 'true'
                const tools = store.getters['login/getConfig'].tools
                const path = router.history.current.path
                const isPlsStockRoute = path && (path.includes('stock-pls') || path.includes('phu-production'))
                const isPlsRoute = path && (path === '/pls' || path.startsWith('/pls/'))
                if (isPlsStockRoute || isPlsRoute) {
                    log('main.js - PLS MODE - NO REDIRECT, NO LOGOUT')
                    return
                }
                if (store.getters['loginUser/getAuditor']) {
                    console.log('logoutAuditorLocal')
                    store.dispatch('login/logoutAuditorLocal').then(() => {
                        router.push('/audit')
                    })
                } else if (plsMode) {
                    // If PLS mode is active, redirect to PLS without logging out
                    console.log('PLS mode active - redirecting to PLS without logout')
                    let route = '/pls'
                    if (tools && tools.includes(20)) {
                        route = '/stock-pls'
                    }
                    router.push(route).catch((err) => {
                        if (err.name == 'NavigationDuplicated') {
                            router.go(router.history.current)
                        }
                    })
                } else if (employee_id) {
                    store.dispatch('loginUser/doLogout', { employee: employee_id }).then(function (response) {
                        if (isSsoLogoutRedirect(response)) {
                            return
                        }
                        router.push(mixin.redirectPath('/home')).catch((err) => {
                            if (err.name == 'NavigationDuplicated') {
                                router.go(router.history.current)
                            }
                        })
                    })
                } else {
                    const target = store.getters.getRouteAfterEmployeeLogout

                    router.push(mixin.redirectPath(target)).catch((err) => {
                        if (err.name == 'NavigationDuplicated') {
                            router.go(router.history.current)
                        }
                    })
                }
            }
            let conn = fnCheckConnection()
            store.commit('setConnState', conn)
        }
    },
    false
)

const events = ['keydown', 'mousedown', 'touchstart']
events.forEach((e) => {
    document.addEventListener(e, function () {
        store.commit('setLastEmployeeAction', { type: e, time: moment().valueOf() })
    })
})

// END SET EVENTS

if (process.env.VUE_APP_IS_APP == 'TRUE') {
    var appRootURL = window.location.href.replace('index.html', '')
    css_image_url = window.location.origin + window.location.pathname.replace('index.html', '') + 'img/'
    window.onerror = function (errorMsg, url, line, col, error) {
        var logMessage = errorMsg
        var stackTrace = null
        log(errorMsg + ' - ' + url + ' - ' + line + ' - ' + col + ' - ' + error)

        var sendError = function () {
            if (store.getters.getIsAndroid && FirebasePlugin) {
                FirebasePlugin.logError(
                    logMessage,
                    stackTrace,
                    function () {
                        log('Sent JS exception')
                    },
                    function (error) {
                        logError('Failed to send JS exception', error)
                    }
                )
            }
        }

        logMessage += ': url=' + url.replace(appRootURL, '') + '; line=' + line + '; col=' + col

        if (typeof error === 'object') {
            StackTrace.fromError(error).then(function (trace) {
                stackTrace = trace
                sendError()
            })
        } else {
            sendError()
        }
    }

    document.addEventListener(
        'deviceready',
        function () {
            log('DEVICE READY')
            console.log('LAUNCH DETAILS', cordova.plugins.notification.local.launchDetails)
            // getLocalIP()
            //     .then((localDeviceIP) => {
            //         console.log('getLocalIP', localDevice)
            //     })
            //     .catch((e) => {
            //         alert('ERROR GETTING LOCAL IP', JSON.stringify(e))
            //     })

            // SETTING PATH FILES FOR APP
            var path = cordova.file.applicationStorageDirectory
            if (store.getters.getIsIOS) path += 'Documents/'
            log('SETTING PATH FILES: ', path)
            store.commit('setPathFiles', path)

            cordova.plugins.brotherprinter.getAutoTimeEnable(
                function (enable) {
                    store.commit('setAutoTimeEnable', enable)
                },
                function (err) {
                    logError('ERROR AL OBTENER EL PARAMETRO' + err.toString())
                }
            )
            cordova.plugins.brotherprinter.getAirPlaneModeEnable(
                function (enable) {
                    store.commit('setAirPlaneModeEnable', enable)
                },
                function (err) {
                    logError('ERROR AL OBTENER EL PARAMETRO' + err.toString())
                }
            )

            function pdMoveInternalFilesToRootDirectory(aFilesToCopy, fCallBack) {
                var permissions = cordova.plugins.permissions
                var list = [permissions.CAMERA, permissions.POST_NOTIFICATIONS, permissions.REQUEST_IGNORE_BATTERY_OPTIMIZATIONS /*, permissions.BLUETOOTH, permissions.BLUETOOTH_ADVERTISE, permissions.BLUETOOTH_SCAN */]

                permissions.checkPermission(list, success, error)

                function error() {
                    log('Camera or Accounts permission is not turned on')
                }

                function success(status) {
                    if (!status.hasPermission) {
                        permissions.requestPermissions(
                            list,
                            function (status) {
                                if (!status.hasPermission) error()

                                notification.init()

                                ble.isLocationEnabled(
                                    function () {
                                        console.log('Permission validated')
                                        ble.scan(
                                            [],
                                            1,
                                            function () {
                                                console.log('FINISH SCAN')
                                                localStorage.setItem('first_time', 1)

                                                getDeviceLocation()

                                                // initRequestBTPermissions()
                                            },
                                            function (err) {
                                                localStorage.setItem('first_time', 1)

                                                getDeviceLocation()

                                                // initRequestBTPermissions()
                                            }
                                        )
                                    },
                                    function () {
                                        console.error('location services are *not* enabled')
                                        localStorage.setItem('first_time', 1)

                                        getDeviceLocation()

                                        // initRequestBTPermissions()
                                    }
                                )
                            },
                            error
                        )
                    } else {
                        getDeviceLocation()
                    }
                }
                // }
            }

            if (typeof localStorage.first_time === 'undefined' || localStorage.first_time == '1') {
                var aFilesToCopy = [
                    // { current_dir: cordova.file.applicationDirectory + 'www/extras/td2120_57mm.bin', root_dir: 'ALEX', name: 'td2120_57mm.bin' },
                    // { current_dir: cordova.file.applicationDirectory + 'www/extras/product2.pdz', root_dir: 'ALEX', name: 'product2.pdz' }
                ]

                pdMoveInternalFilesToRootDirectory(aFilesToCopy)

                // setTimeout(function() {console.log("ERROR THROW");throw new Error()}, 30);

                new Vue({
                    el: '#app',
                    i18n,
                    store,
                    router,
                    moment,
                    components: { App },
                    template: '<App/>',
                    mixins: [mixin]
                    /* mounted() {
                        let recaptchaScript = document.createElement('script')
                        recaptchaScript.setAttribute('src', '~@/core/store.js')
                        document.head.appendChild(recaptchaScript)
                      }, */
                })
            }
        },
        false
    )
} else {
    store.commit('printer/setDeviceNewConfiguration', true)
    new Vue({
        el: '#app',
        i18n,
        store,
        router,
        moment,
        components: { App },
        template: '<App/>',
        mixins: [mixin]
        /* mounted() {
              let recaptchaScript = document.createElement('script')
              recaptchaScript.setAttribute('src', '~@/core/store.js')
              document.head.appendChild(recaptchaScript)
            }, */
    })
}

function getDeviceLocation() {
    navigator.geolocation.getCurrentPosition(
        function (position) {
            log('GETTING POSITION: ', position)
            var coords = { latitude: position.coords.latitude, longitude: position.coords.longitude }
            store.commit('setGeoLocation', coords)
        },
        function (error) {
            log('ERROR GEOLOCATION -> ', error)
            if (error.code == 3) {
                setTimeout(getDeviceLocation, 10 * 60 * 1000)
            }
        },
        { timeout: 3000 }
    )
}
